/**
 * VenueBannerUpload component.
 * Displays a rectangular venue banner with an overlay that lets users
 * pick and upload a new banner image to Supabase Storage.
 */

import { useRef, useState } from 'react';
import { supabase } from '../utils/supabase';
import '../styles/VenueBannerUpload.css';

interface VenueBannerUploadProps {
  currentUrl: string | null;
  venueId: string;
  onUploadComplete: (url: string) => void;
  aspectRatio?: string; // e.g. "16 / 9"
}

export default function VenueBannerUpload({
  currentUrl,
  venueId,
  onUploadComplete,
  aspectRatio = '16 / 9',
}: VenueBannerUploadProps) {
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleBoxClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Basic validation
    const allowedTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];
    if (!allowedTypes.includes(file.type)) {
      setError('Please select a valid image (PNG, JPEG, WebP, GIF).');
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      setError('Image must be smaller than 5MB.');
      return;
    }

    try {
      setIsUploading(true);
      setError(null);

      const ext = file.name.split('.').pop();
      const filePath = `${venueId}/banner.${ext}`;

      // Upload to 'venues' bucket
      const { data, error: uploadError } = await supabase.storage
        .from('venues')
        .upload(filePath, file, {
          cacheControl: '3600',
          upsert: true,
        });

      if (uploadError) throw uploadError;

      // Get public URL
      const { data: urlData } = supabase.storage.from('venues').getPublicUrl(data.path);

      // Append timestamp to prevent showing caching of the old image
      const publicUrl = `${urlData.publicUrl}?t=${Date.now()}`;
      onUploadComplete(publicUrl);
    } catch (err: any) {
      console.error('Upload error:', err);
      setError(err.message || 'Failed to upload image.');
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <div className="venue-banner-upload-wrapper">
      <div
        className={`venue-banner-container ${isUploading ? 'uploading' : ''}`}
        style={{ aspectRatio }}
        onClick={handleBoxClick}
      >
        {currentUrl ? (
          <img
            onLoad={(e) => e.currentTarget.classList.add('loaded')}
            src={currentUrl}
            alt="Venue Banner"
            className="venue-banner-img"
          />
        ) : (
          <div className="venue-banner-placeholder">
            <span>Click to upload banner</span>
          </div>
        )}

        <div className="venue-banner-overlay">
          <div className="venue-banner-overlay-content">
            <svg
              viewBox="0 0 24 24"
              width="32"
              height="32"
              stroke="currentColor"
              strokeWidth="2"
              fill="none"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"></path>
              <circle cx="12" cy="13" r="4"></circle>
            </svg>
            <span>Change Banner</span>
          </div>
        </div>

        {isUploading && (
          <div className="venue-banner-loading">
            <div className="venue-banner-spinner"></div>
          </div>
        )}
      </div>

      {error && <p className="venue-banner-error">{error}</p>}

      <input
        type="file"
        ref={fileInputRef}
        onChange={handleFileChange}
        accept="image/*"
        style={{ display: 'none' }}
      />
    </div>
  );
}
